import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";

const QA = [
  {
    keywords: ["cos'è il certificato", "cos e il certificato", "certificato di agibilità", "agibilità", "agibilita"],
    question: "Cos'è il certificato di agibilità?",
    answer:
      "Il certificato di agibilità è il documento previdenziale necessario per le prestazioni artistiche nel mondo dello spettacolo. ENPALSBOX ti aiuta a richiederlo: compila il modulo dalla tua Area Personale con i dati del locale, della data e dell'importo pattuito, e al resto pensiamo noi.",
  },
  {
    keywords: ["richiedere un certificato", "richiedere", "come richiedo", "richiesta certificato"],
    question: "Come posso richiedere un certificato?",
    answer:
      "Per richiedere un certificato di agibilità: 1) Iscriviti gratuitamente a ENPALSBOX. 2) Accedi alla tua Area Personale. 3) Clicca su 'Richiedi Certificato di Agibilità' e compila il modulo con i dati del locale e della tua esibizione. 4) Invia la richiesta: riceverai una email di conferma con il numero di pratica.",
  },
  {
    keywords: ["di cosa si occupa", "cosa fate", "cosa fa enpalsbox", "chi siete", "enpalsbox"],
    question: "Di cosa si occupa ENPALSBOX?",
    answer:
      "ENPALSBOX offre assistenza ad artisti e lavoratori dello spettacolo per gli adempimenti previdenziali collegati alle loro attività: richiesta del certificato di agibilità, informazioni su F24 e dichiarazione mensile, e supporto dedicato. Tu pensa alla musica, alla burocrazia pensiamo noi.",
  },
  {
    keywords: ["iscriver", "registr", "iscrizione", "creare un account", "account"],
    question: "Come posso iscrivermi?",
    answer:
      "Per iscriverti clicca su ISCRIVITI nel menu in alto e compila il modulo con Nome, Cognome, Codice Fiscale, Email e Password. L'iscrizione è immediata: subito dopo potrai accedere alla tua Area Personale.",
  },
  {
    keywords: ["accedere", "accedo", "login", "entrare", "password"],
    question: "Come posso accedere?",
    answer:
      "Per accedere clicca su ACCEDI nel menu in alto e inserisci l'email e la password scelte in fase di iscrizione. Dopo l'accesso vedrai la tua Area Personale con i tuoi dati e le tue richieste.",
  },
  {
    keywords: ["f24", "modello f24"],
    question: "Cos'è l'F24?",
    answer:
      "L'F24 è il modello utilizzato in Italia per il versamento di imposte, contributi e premi assicurativi, e può essere collegato agli adempimenti previdenziali e fiscali dell'attività dello spettacolo. Per maggiori dettagli visita la pagina F24 del sito; per la tua situazione specifica contatta direttamente ENPALSBOX.",
  },
  {
    keywords: ["dichiarazione mensile", "dichiarazione"],
    question: "Cos'è la dichiarazione mensile?",
    answer:
      "La dichiarazione mensile è l'adempimento con cui vengono comunicate le prestazioni artistiche rese nel mese, rilevante per gli obblighi previdenziali legati allo spettacolo. Trovi una spiegazione semplice nella pagina 'Dichiarazione Mensile' del sito; per dubbi specifici contatta ENPALSBOX.",
  },
  {
    keywords: ["unilav", "uni-lav", "esente", "esenzione", "esonero", "dipendente", "pensionato"],
    question: "Sono dipendente o pensionato: posso essere esente?",
    answer:
      "Se sei già dipendente o pensionato puoi essere esente dagli adempimenti ENPALS: in questo caso puoi richiedere la comunicazione UniLAV scrivendoci direttamente via email a enpalsbox@enpalsbox.it. Ti risponderemo con tutte le indicazioni necessarie.",
  },
  {
    keywords: ["contattare", "contatti", "contattaci", "telefono", "email di enpalsbox", "parlare"],
    question: "Come posso contattare ENPALSBOX?",
    answer: "contact",
  },
  {
    keywords: ["mie richieste", "vedo le mie richieste", "stato della richiesta", "pratica", "storico"],
    question: "Come vedo le mie richieste?",
    answer:
      "Accedi alla tua Area Personale e clicca su 'Le mie richieste': troverai l'elenco completo delle tue pratiche con numero, data, locale, importo e stato aggiornato (Richiesta ricevuta, In lavorazione, Approvata, Completata o Annullata).",
  },
];

const FALLBACK =
  "Non sono in grado di rispondere con certezza a questa domanda. Puoi contattare direttamente ENPALSBOX.";

const QUICK = [
  "Cos'è il certificato di agibilità?",
  "Come posso richiedere un certificato?",
  "Cos'è l'F24?",
  "Come posso iscrivermi?",
  "Sono esente: cos'è l'UniLAV?",
  "Contatta ENPALSBOX",
];

function ChatBubble({ message, onClose }) {
  return (
    <div className={`flex ${message.from === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
          message.from === "user" ? "bg-bordeaux text-white" : "bg-muted text-foreground"
        }`}
      >
        {message.text}
        {message.contact && (
          <Link
            to="/contatti"
            onClick={onClose}
            data-testid="chatbot-contact-link"
            className="mt-2 block rounded-lg bg-bordeaux px-3 py-1.5 text-center text-xs font-semibold text-white hover:bg-bordeaux-hover"
          >
            CONTATTA ENPALSBOX
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const idRef = useRef(1);
  const [messages, setMessages] = useState([
    { id: 0, from: "bot", text: "Ciao! Sono l'assistente virtuale di ENPALSBOX. Come posso aiutarti?" },
  ]);
  const [input, setInput] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const reply = (text) => {
    const q = text.toLowerCase();
    if (q.includes("contatta enpalsbox")) {
      return { text: "Puoi scriverci direttamente compilando il modulo contatti: ti risponderemo il prima possibile.", contact: true };
    }
    const found = QA.find((item) => item.keywords.some((k) => q.includes(k)));
    if (!found) return { text: FALLBACK, contact: true };
    if (found.answer === "contact") {
      return { text: "Puoi contattare ENPALSBOX in qualsiasi momento tramite il modulo contatti: compila nome, email e messaggio e ti risponderemo al più presto.", contact: true };
    }
    return { text: found.answer };
  };

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value) return;
    const r = reply(value);
    const userMsg = { id: idRef.current++, from: "user", text: value };
    const botMsg = { id: idRef.current++, from: "bot", text: r.text, contact: r.contact };
    setMessages((m) => [...m, userMsg, botMsg]);
    setInput("");
  };

  return (
    <>
      <button
        data-testid="chatbot-open-trigger"
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-bordeaux text-white shadow-lg shadow-bordeaux/30 transition-[transform,box-shadow,background-color] duration-300 hover:scale-105 hover:bg-bordeaux-hover hover:shadow-xl hover:shadow-bordeaux/40"
        aria-label="Apri assistente ENPALSBOX"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-4 z-50 flex h-[28rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border border-border bg-white shadow-2xl animate-scale-in sm:right-5"
        >
          <div className="bg-bordeaux px-4 py-3">
            <p className="font-heading text-sm font-bold text-white">Assistente ENPALSBOX</p>
            <p className="text-xs text-white/70">Risposte immediate alle domande più frequenti</p>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-3" data-testid="chatbot-messages">
            {messages.map((m) => (
              <ChatBubble key={m.id} message={m} onClose={() => setOpen(false)} />
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-border p-2">
            <div className="mb-2 flex flex-wrap gap-1.5">
              {QUICK.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  data-testid={`chatbot-quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                  className="rounded-full border border-bordeaux/30 bg-bordeaux-ultralight px-2.5 py-1 text-[11px] font-medium text-bordeaux transition-colors hover:bg-bordeaux hover:text-white"
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Scrivi la tua domanda..."
                className="input-elegant !py-2 text-sm"
              />
              <button
                data-testid="chatbot-send-button"
                onClick={() => send()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bordeaux text-white hover:bg-bordeaux-hover"
                aria-label="Invia"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
